import { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Alert as MuiAlert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  TextField,
} from '@mui/material';
import { useApi } from '../../hooks/useApi';
import { BillingProfileSummary } from '../../types/billing';
import { EthocaMisroutedDescriptor, EthocaMisroutedDescriptorSaveResult } from '../../types/ethocaMisroutedDescriptor';

const MISROUTED_DESCRIPTORS_API_URL = '/api/alerts/ethoca/misrouted-descriptors';

interface MisroutedDescriptorDialogProps {
  open: boolean;
  companyId: number;
  initialDescriptor: string;
  misroutedDescriptor?: EthocaMisroutedDescriptor;
  onClose: () => void;
  onSaved: (billedAlertsCount?: number) => void;
}

export default function MisroutedDescriptorDialog({
  open,
  companyId,
  initialDescriptor,
  misroutedDescriptor,
  onClose,
  onSaved,
}: MisroutedDescriptorDialogProps) {
  const { get, loading: profilesLoading } = useApi();
  const { post, put, loading, error } = useApi();
  const [descriptorPrefix, setDescriptorPrefix] = useState(misroutedDescriptor?.descriptor_prefix ?? initialDescriptor);
  const [billingProfileId, setBillingProfileId] = useState<number | ''>(misroutedDescriptor?.billing_profile_id ?? '');
  const [billingProfiles, setBillingProfiles] = useState<BillingProfileSummary[]>([]);
  const [profilesError, setProfilesError] = useState<string | null>(null);

  const fetchBillingProfiles = useCallback(async () => {
    setProfilesError(null);
    try {
      const profiles = await get<BillingProfileSummary[]>(`/api/companies/${companyId}/billing-profiles`);
      setBillingProfiles(profiles);
    } catch (err: any) {
      setProfilesError(err.message || 'Failed to load billing profiles');
    }
  }, [get, companyId]);

  useEffect(() => {
    if (!open) return;
    setDescriptorPrefix(misroutedDescriptor?.descriptor_prefix ?? initialDescriptor);
    setBillingProfileId(misroutedDescriptor?.billing_profile_id ?? '');
    fetchBillingProfiles();
  }, [open, fetchBillingProfiles, misroutedDescriptor, initialDescriptor]);

  const handleClose = (_event: unknown, reason?: string) => {
    if (reason === 'backdropClick') return;
    onClose();
  };

  const handleSubmit = async () => {
    const trimmed = descriptorPrefix.trim();
    if (!trimmed || billingProfileId === '' || loading) return;
    try {
      const body = { descriptorPrefix: trimmed, billingProfileId };
      const result = misroutedDescriptor
        ? await put<EthocaMisroutedDescriptorSaveResult>(`${MISROUTED_DESCRIPTORS_API_URL}/${misroutedDescriptor.ethoca_misrouted_descriptor_id}`, body)
        : await post<EthocaMisroutedDescriptorSaveResult>(MISROUTED_DESCRIPTORS_API_URL, body);
      onSaved(result.billedAlertsCount);
    } catch (err) {
      console.error('Failed to save misrouted descriptor:', err);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} aria-labelledby="misrouted-descriptor-dialog-title" maxWidth="xs" fullWidth>
      <DialogTitle id="misrouted-descriptor-dialog-title">
        {misroutedDescriptor ? 'Edit Misrouted Descriptor' : 'Mark Descriptor as Misrouted'}
      </DialogTitle>
      <DialogContent>
        {(error || profilesError) && (
          <MuiAlert severity="error" sx={{ mb: 2 }}>
            {error || profilesError}
          </MuiAlert>
        )}
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Descriptor prefix"
            value={descriptorPrefix}
            onChange={(e) => setDescriptorPrefix(e.target.value)}
            disabled={loading}
            helperText="Alerts whose descriptor starts with this text bill to the chosen profile."
          />
          <FormControl size="small" fullWidth disabled={loading || profilesLoading || billingProfiles.length === 0}>
            <InputLabel id="misrouted-descriptor-billing-profile-label">Billing profile</InputLabel>
            <Select
              labelId="misrouted-descriptor-billing-profile-label"
              value={billingProfileId}
              label="Billing profile"
              onChange={(e: SelectChangeEvent<number | ''>) => setBillingProfileId(e.target.value === '' ? '' : Number(e.target.value))}
            >
              {billingProfiles.map((profile) => (
                <MenuItem key={profile.billingProfileId} value={profile.billingProfileId}>
                  {profile.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading || !descriptorPrefix.trim() || billingProfileId === ''}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
